import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  // RadioGroup,
  // FormControlLabel,
  // Radio,
  // FormLabel,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  // Upload as UploadIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material'
import CampaignMessageStatsTab from '../components/campaign/CampaignMessageStatsTab'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

import { useNotification } from '../contexts/NotificationContext'
import campaignApi, { Campaign, CampaignStats, LeadLevelStats, ExecutionLog, Message, VariableMapping } from '../services/campaignApi'
import { templatesApi } from '../services/api'
import ConfirmDialog from '../components/dialogs/ConfirmDialog'
import ConversationDetailDialog from '../components/dialogs/ConversationDetailDialog'
import TimezoneSelector from '../components/timezone'
import { formatTime12Hour, formatDateForInput, formatTimeForInput } from '../utils/textParser'
import { getDefaultStartDate, getDefaultEndDate } from '../utils/dateUtils'
import TemplatePreview from '../components/templates/TemplatePreview'
import PageBanner from '../components/PageBanner'
import { PAGE_BANNER_SUBHEADING_SX, PAGE_BANNER_PRIMARY_BTN_SX } from '../theme/constants'
import CampaignStatusChip from '../components/campaign/CampaignStatusChip'
import ReactMarkdown from 'react-markdown'


interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const CampaignDetail: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const [loading, setLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [leadStats, setLeadStats] = useState<LeadLevelStats | null>(null)
  const [campaignStats, setCampaignStats] = useState<CampaignStats | null>(null)
  const [executionLogs, setExecutionLogs] = useState<ExecutionLog[]>([])
  const [executionLogsLoading, setExecutionLogsLoading] = useState(false)
  const [executionLogsLoaded, setExecutionLogsLoaded] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [isDownloadingStats, setDownloadingStats] = useState(false)
  
  // Section-based edit state
  const [editingSection, setEditingSection] = useState<string | null>(null) // 'info' | 'template' | 'audience' | 'schedule' | null
  const [editData, setEditData] = useState<any>({})
  const [templates, setTemplates] = useState<any[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const [templateVariables, setTemplateVariables] = useState<any[]>([])
  const [csvFile, setCsvFile] = useState<File | null>(null)
  // const [existingLeadbook, setExistingLeadbook] = useState<any>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cooldownCampaignDownloadStats, setCooldownCampaignDownloadStats] = useState<number>(15)
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)

  const handleOpenConversation = (msg: Message) => {
    if (!msg.conversation_id) {
      showNotification('No conversation started yet.', 'info')
      return
    }

    setSelectedConversationId(msg.conversation_id)
    setConversationDialogOpen(true)
  }

  const handleCloseConversationDialog = () => {
    setConversationDialogOpen(false)
    setSelectedConversationId(null)
  }

  useEffect(() => {
    if (id) {
      loadAllData()
    }
  }, [id])

  useEffect(() => {
    if (editingSection === 'template' && campaign) {
      // Load templates list for the dropdown
      loadTemplates()
      // If we already have template details from API, use them
      // Otherwise, load template details if template_id exists
      if (campaign.template_id && !selectedTemplate) {
        loadTemplateDetails(campaign.template_id)
      }
    }
  }, [editingSection, campaign])

  const loadCampaign = async () => {
    if (!id) return
    try {
      setLoading(true)
      const data = await campaignApi.getCampaign(id)
      setCampaign(data)
      
      // Load template details if available from API response
      if ((data as any).template) {
        setSelectedTemplate((data as any).template)
        // Load template variables if template_variables_metadata exists
        if ((data as any).template.template_variables_metadata) {
          const variables = Object.keys((data as any).template.template_variables_metadata).map((key, index) => ({
            index: index + 1,
            name: key,
            label: (data as any).template.template_variables_metadata[key],
          }))
          setTemplateVariables(variables)
        }
      }

      if((data as any).campaign_download_stats_cooldown) {
        setCooldownCampaignDownloadStats((data as any).campaign_download_stats_cooldown)
      }
      
      setEditData({
        name: data.name,
        description: (data as any).description || '',
        campaign_type: data.campaign_type,
        template_id: data.template_id || '',
        // Use same defaults as CampaignWizard step 4
        timezone: data.timezone || 'IST',
        start_at: data.start_at ? new Date(data.start_at).toISOString().slice(0, 16) : getDefaultStartDate(),
        end_at: (data as any).end_at ? new Date((data as any).end_at).toISOString().slice(0, 16) : getDefaultEndDate(),
        send_window_start: (data as any).send_window_start || '09:00',
        send_window_end: (data as any).send_window_end || '18:00',
        variable_mappings: (data as any).template_variable_mappings || [],
      })
      
      // if ((data as any).leadbook) {
      //   setExistingLeadbook((data as any).leadbook)
      // }
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to load campaign', 'error')
    } finally {
      setLoading(false)
    }
  }

  const loadTemplates = async () => {
    if (!campaign) return
    try {
      const response = await templatesApi.getTemplates({
        smb_id: campaign.smb_id,
        status: ['approved'],
        limit: 100,
      })
      setTemplates(response.templates || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  const loadTemplateDetails = async (templateId: string) => {
    try {
      const response = await templatesApi.getTemplateVariables(templateId)
      setTemplateVariables(response.variables || [])
      const template = templates.find((t) => t.id === templateId)
      if (template) {
        setSelectedTemplate(template)
      }
      return response
    } catch (error) {
      console.error('Failed to load template details:', error)
      return null
    }
  }

  const loadStats = async () => {
    if (!id) return
    try {
      const [leadStatsData, msgStatsData] = await Promise.all([
        campaignApi.getCampaignLeadStats(id),
        campaignApi.getCampaignStats(id)
      ])
      setLeadStats(leadStatsData)
      setCampaignStats(msgStatsData)
    } catch (error: any) {
      console.error('Failed to load stats:', error)
    }
  }

  const loadExecutionLogs = async () => {
    if (!id) return
    try {
      setExecutionLogsLoading(true)
      const response = await campaignApi.getExecutionLogs(id, { limit: 20 })
      setExecutionLogs(response.execution_logs || [])
      setExecutionLogsLoaded(true)
    } catch (error: any) {
      console.error('Failed to load execution logs:', error)
    } finally {
      setExecutionLogsLoading(false)
    }
  }

  // Lazy-load execution logs when shifting to Execution Logs tab (index 1)
  useEffect(() => {
    if (activeTab === 1 && id && !executionLogsLoaded) {
      loadExecutionLogs()
    }
  }, [activeTab, id, executionLogsLoaded])

  const loadAllData = async (isRefresh: boolean = false) => {
    if (!id) return
    if (isRefresh) {
      setIsRefreshing(true)
    } else {
      setLoading(true)
    }
    try {
      const promises: Promise<any>[] = [
        loadCampaign(),
        loadStats(),
      ]
      // Only reload execution logs if the user is currently viewing the execution logs tab
      if (activeTab === 1) {
        promises.push(loadExecutionLogs())
      }
      await Promise.all(promises)
    } catch (error) {
      console.error('Error loading campaign data:', error)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }

  const handleCancel = async () => {
    if (!id) return
    try {
      await campaignApi.cancelCampaign(id)
      showNotification('Campaign cancelled successfully', 'success')
      loadCampaign()
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to cancel campaign', 'error')
    }
  }

  const handlePauseAndEdit = async () => {
    if (!id || !campaign) return
    if (isExpired) {
      showNotification('This campaign has expired and cannot be paused or edited.', 'warning')
      return
    }
    try {
      // Check if there's a running execution log
      const runningLog = campaign.latest_execution_log?.status === 'running' 
        ? campaign.latest_execution_log 
        : executionLogs.find(log => log.status === 'running')
      if (runningLog) {
        showNotification('Campaign is running now. You can pause later.', 'warning')
        return
      }
      
      // Check if campaign can be paused
      if (campaign.campaign_status !== 'scheduled' && campaign.campaign_status !== 'partially_completed') {
        showNotification('Only scheduled or partially completed campaigns can be paused', 'error')
        return
      }
      
      await campaignApi.pauseCampaign(id)
      showNotification('Campaign paused. You can now edit the details.', 'success')
      await loadCampaign()
      if (activeTab === 1) {
        await loadExecutionLogs()
      }
      // Campaign will be paused, so canEdit will become true
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to pause campaign', 'error')
    }
  }

  const handleResume = async () => {
    if (!id || !campaign) return
    if (isExpired) {
      showNotification('This campaign has expired and cannot be resumed.', 'warning')
      return
    }
    try {
      await campaignApi.resumeCampaign(id)
      showNotification('Campaign resumed successfully', 'success')
      await loadCampaign()
      if (activeTab === 1) {
        await loadExecutionLogs()
      }
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to resume campaign', 'error')
    }
  }


  const handleTemplateChange = async (templateId: string) => {
    setEditData({ ...editData, template_id: templateId })
    const response = await loadTemplateDetails(templateId)
    
    // Initialize variable mappings only if they don't exist
    // loadTemplateDetails updates templateVariables state, so we need to use the response
    if (response && response.variables) {
      const currentMappings = editData.variable_mappings || []
      if (currentMappings.length === 0 && response.variables.length > 0) {
        const mappings: VariableMapping[] = response.variables.map((v: any) => ({
          csv_column: '',
          template_var: `var_${v.index}`,
          user_label: `Field ${v.index}`,
        }))
        setEditData({ ...editData, template_id: templateId, variable_mappings: mappings })
      }
    }
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await campaignApi.deleteCampaign(id)
      showNotification('Campaign deleted successfully', 'success')
      navigate('/campaigns')
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to delete campaign', 'error')
    }
    setDeleteDialogOpen(false)
  }

  const handleSectionEdit = (section: string) => {
    if (!campaign) return
    if (isExpired) {
      showNotification('This campaign has expired and cannot be edited.', 'warning')
      return
    }
    setEditingSection(section)
    // Initialize edit data based on section
    if (section === 'info') {
      setEditData({
        name: campaign.name,
        campaign_type: campaign.campaign_type,
        description: (campaign as any).description,
      })
    } else if (section === 'template') {
      setEditData({
        template_id: campaign.template_id || '',
        variable_mappings: (campaign as any).template_variable_mappings || [],
      })
    } else if (section === 'audience') {
      setEditData({
        audience_type: (campaign as any).audience_filter?.audience_type || 'upload',
      })
      setCsvFile(null)
    } else if (section === 'schedule') {
      setEditData({
        // Use current state to preserve any changes or fallback to campaign data
        start_at: campaign.start_at ? formatDateForInput(campaign.start_at) : getDefaultStartDate(),
        end_at: (campaign as any).end_at ? formatDateForInput((campaign as any).end_at) : getDefaultEndDate(),
        timezone: campaign.timezone || 'IST',
        send_window_start: formatTimeForInput((campaign as any).send_window_start),
        send_window_end: formatTimeForInput((campaign as any).send_window_end),
        description: (campaign as any).description || 'No Description',
      })
    }
  }

  const handleSectionSave = async (section: string) => {
    if (!id || !campaign) return
    try {
      setSaving(true)

      if (section === 'info') {
        await campaignApi.updateCampaign(id, {
          name: editData.name,
          description: editData.description,
        })
      } else if (section === 'template') {
        // Validate template is selected
        if (!editData.template_id) {
          showNotification('Please select a template', 'error')
          return
        }
        if (editData.template_id && editData.template_id !== campaign.template_id) {
          const variableMappings = editData.variable_mappings && editData.variable_mappings.length > 0
            ? editData.variable_mappings
            : []
          await campaignApi.attachTemplate(id, editData.template_id, variableMappings)
        }
      } else if (section === 'audience') {
        // If switching to master or already master, update schedule with audience_type
        if (editData.audience_type === 'master') {
          // Update schedule to set audience_type to master
          await campaignApi.scheduleCampaign(id, {
            start_at: campaign.start_at ? new Date(campaign.start_at).toISOString() : new Date().toISOString(),
            timezone: campaign.timezone || 'UTC',
            audience_type: 'master',
          })
        } else if (csvFile) {
          // Upload new CSV file
          const columnMappings: Record<string, string> = {
            name: 'name',
            phone: 'phone',
          }
          await campaignApi.uploadAudience(id, csvFile, columnMappings)
          // Also update schedule to set audience_type to upload
          if (campaign.start_at) {
            await campaignApi.scheduleCampaign(id, {
              start_at: new Date(campaign.start_at).toISOString(),
              timezone: campaign.timezone || 'UTC',
              audience_type: 'upload',
            })
          }
        }
        // If no csvFile and not master, do nothing (user might just be viewing)
      } else if (section === 'schedule') {
        // Validate end date is present
        if (!editData.end_at) {
          showNotification('End date is required', 'error')
          return
        }

        // Validate start_at is present for non-partially-completed campaigns
        if (!wasPartiallyCompleted && !editData.start_at) {
          showNotification('Start date is required', 'error')
          return
        }
        // Validate date relationships only if start_at is being updated
        if (!wasPartiallyCompleted && editData.end_at && editData.start_at) {
          const startDate = new Date(editData.start_at)
          const endDate = new Date(editData.end_at)
          if (endDate <= startDate) {
            showNotification('End date must be after start date', 'error')
            return
          }
        }
        // Validate end date is not in the past
        if (new Date(editData.end_at) <= new Date()) {
          showNotification('End date cannot be in the past', 'error')
          return
        }
        try {
          let scheduleData: any = {}

          if (wasPartiallyCompleted) {
            // Partially completed campaigns can ONLY update these 3 fields
            // Defaulting to existing values or standard defaults if missing
            const startWindow = editData.send_window_start || (campaign as any).send_window_start || '09:00'
            const endWindow = editData.send_window_end || (campaign as any).send_window_end || '18:00'
            const endDate = editData.end_at || (campaign as any).end_at

            scheduleData = {
              end_at: endDate && endDate.length === 16 ? `${endDate}:00` : endDate,
              send_window_start: startWindow.substring(0, 5),
              send_window_end: endWindow.substring(0, 5),
            }
            console.log('Updating partially completed campaign record with data:', scheduleData)
            await campaignApi.updateCampaign(id, scheduleData)
          } else {
            // Draft/Scheduled campaigns can update everything
            const startWindow = editData.send_window_start || '09:00'
            const endWindow = editData.send_window_end || '18:00'

            scheduleData = {
              timezone: editData.timezone || campaign.timezone || 'IST',
              start_at: editData.start_at ? (editData.start_at.length === 16 ? `${editData.start_at}:00` : editData.start_at) : campaign.start_at,
              end_at: editData.end_at ? (editData.end_at.length === 16 ? `${editData.end_at}:00` : editData.end_at) : (campaign as any).end_at,
              send_window_start: startWindow.substring(0, 5),
              send_window_end: endWindow.substring(0, 5),
            }
            console.log('Scheduling campaign with data:', scheduleData)
            await campaignApi.scheduleCampaign(id, scheduleData)
          }

        } catch (error: any) {
          showNotification(error.response?.data?.detail || 'Please check your dates.', 'error')
          return
        }
      }

      if (section === 'schedule' && wasPartiallyCompleted) {
        showNotification('Schedule updated. Please refresh the page to see the changes.', 'success')
      } else {
        showNotification(`${section.charAt(0).toUpperCase() + section.slice(1)} updated successfully`, 'success')
      }
      setEditingSection(null)
      setEditData({})
      setCsvFile(null)
      loadCampaign()
    } catch (error: any) {
      showNotification(error.response?.data?.detail || `Failed to update ${section}`, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDownloadStats = async () => {
    if (isDownloadingStats) {
      showNotification("Please Wait, Downloading is in progress...","warning")
      return
    }
    if (!id || !campaign) return

    if (!campaign.latest_execution_log && !executionLogs?.length) {
      showNotification("Campaign Has no execution, stats can only be downloaded if atleast there is one execution",'error')
      return
    }

    if (campaign.campaign_status == 'running'){
      showNotification("Campaign is running, stats can't be download while campaign is running",'warning')
      return
    }

    try {
      setDownloadingStats(true)
      const downloadUrl = await campaignApi.downloadStats(id)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = ''
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      showNotification("Stats Download Successfully!", "success")
    } catch (error: any) {
      let errorPayload = error.response?.data
      const errorMessage = errorPayload?.error?.message || error.message
      showNotification(errorMessage || "Please try again, something went wrong!","error")
    }
    finally {
      setDownloadingStats(false)
    }
  }

  const handleSectionCancel = () => {
    setEditingSection(null)
    setEditData({})
    setCsvFile(null)
  }

  if (loading && !campaign) {
    return (
      <Box>
        <LinearProgress />
        <Typography>Loading campaign...</Typography>
      </Box>
    )
  }

  if (!campaign) {
    return (
      <Box>
        <Typography>Campaign not found</Typography>
      </Box>
    )
  }

  // Check if campaign is expired
  const isExpired = campaign.end_at ? new Date(campaign.end_at) < new Date() : false

  // Check if campaign can be edited (draft, paused, or was partially completed)
  const canEdit = (campaign.campaign_status === 'draft' ||
    campaign.campaign_status === 'paused') && !isExpired

  // Check if campaign has a running execution log
  const hasRunningExecution = campaign.latest_execution_log?.status === 'running' || executionLogs.some(log => log.status === 'running')

  // Check if campaign can be paused (scheduled or partially_completed, and not running)
  const canPause = (campaign.campaign_status === 'scheduled' || campaign.campaign_status === 'partially_completed') && !hasRunningExecution && !isExpired

  // Check if campaign can be resumed
  const canResume = campaign.campaign_status === 'paused' && !isExpired

  // Check if campaign was partially completed (for restricted editing)
  const wasPartiallyCompleted = campaign.status_before_pause === 'partially_completed'

  console.log('wasPartiallyCompleted', wasPartiallyCompleted)

  // Check if campaign is running (has running execution log or status is running)
  const isRunning = campaign.campaign_status === 'running' || hasRunningExecution

  // Get latest execution log for partially completed campaigns
  const latestExecutionLog = campaign.latest_execution_log || (executionLogs.length > 0 ? executionLogs[0] : null)

  const getDisplayStatus = (status: string): string => {
    if (status === 'partially_completed' &&
      latestExecutionLog?.next_retry_at &&
      leadStats?.leads_delivered === 0 &&
      (campaign as any).end_at &&
      new Date((campaign as any).end_at) > new Date()) {
      return 'queued_for_retry'
    }

    if (status === 'completed' &&
      leadStats?.leads_delivered === 0 &&
      leadStats?.leads_failed > 0) {
      return 'failed'
    }

    return status
  }

  const displayStatus = getDisplayStatus(campaign.campaign_status)
  const canDownloadStats = latestExecutionLog && latestExecutionLog.status !== 'running'

  return (
    <Box>
      {/* Header */}
      <PageBanner
        title={campaign.name}
        statusChip={<CampaignStatusChip status={displayStatus} variant="banner" />}
        subtitle={
          latestExecutionLog && (
            <Box sx={{ mt: 0.5 }}>
              {latestExecutionLog.execution_started_at && (
                <Typography sx={PAGE_BANNER_SUBHEADING_SX}>
                  Last Run At: {format(new Date(latestExecutionLog.execution_started_at), 'MMM dd, yyyy • h:mm a')}
                </Typography>
              )}
              {/* Show Next Scheduled Run only for non-cancelled campaigns */}
              {latestExecutionLog.next_retry_at && campaign.campaign_status !== 'cancelled' && (
                <Typography sx={PAGE_BANNER_SUBHEADING_SX}>
                  Next Scheduled Run At: {format(new Date(latestExecutionLog.next_retry_at), 'MMM dd, yyyy • h:mm a')}
                </Typography>
              )}

              {/* Show Cancelled At for cancelled campaigns */}
              {campaign.campaign_status === 'cancelled' && campaign.updated_at && (
                <Typography sx={PAGE_BANNER_SUBHEADING_SX}>
                  Cancelled At: {format(new Date(campaign.updated_at), 'MMM dd, yyyy • h:mm a')}
                </Typography>
              )}
            </Box>
          )
        }
        action={
          <>
            {isRunning && (
              <Alert 
                severity="warning" 
                sx={{ 
                  width: '100%',
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  border: '1px solid rgba(255,255,255,0.3)',
                  '& .MuiAlert-icon': { color: 'white' }
                }}
              >
                Campaign is currently running. Actions are disabled.
              </Alert>
            )}
            {!isRunning && canDownloadStats && (
              <Tooltip title={`Data refreshes after every ${cooldownCampaignDownloadStats} minutes`} arrow slotProps={{ tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }}>
                <span>
                  <Button
                    variant="contained"
                    fullWidth={isMobile}
                    startIcon={
                      isDownloadingStats ? (
                        <CircularProgress size={18} sx={{ color: 'primary.main' }} />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    sx={{
                      ...PAGE_BANNER_PRIMARY_BTN_SX,
                      color: 'primary.main',
                    }}
                    onClick={handleDownloadStats}
                    disabled={isDownloadingStats}
                  >
                    Download Stats
                  </Button>
                </span>
              </Tooltip>
            )}
            {!isRunning && canPause && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                startIcon={<PauseIcon />}
                onClick={handlePauseAndEdit}
                sx={{
                  ...PAGE_BANNER_PRIMARY_BTN_SX,
                  color: 'warning.main',
                }}
              >
                {isMobile ? 'Pause' : 'Pause & Edit'}
              </Button>
            )}
            {canResume && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                startIcon={<PlayArrowIcon />}
                onClick={handleResume}
                sx={{
                  ...PAGE_BANNER_PRIMARY_BTN_SX,
                  color: 'success.main',
                }}
              >
                Resume
              </Button>
            )}
            {['scheduled', 'running', 'paused', 'partially_completed'].includes(campaign.campaign_status) && !isRunning && !isExpired && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                sx={{
                  ...PAGE_BANNER_PRIMARY_BTN_SX,
                  color: 'error.main',
                }}
              >
                {isMobile ? 'Cancel' : 'Cancel Campaign'}
              </Button>
            )}
            {!isRunning && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  ...PAGE_BANNER_PRIMARY_BTN_SX,
                  bgcolor: 'rgba(211, 47, 47, 0.9)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 1)' },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(211, 47, 47, 0.4)',
                    color: 'rgba(255, 255, 255, 0.6)',
                    borderColor: 'transparent',
                  },
                }}
              >
                Delete
              </Button>
            )}
          </>
        }
        onRefresh={() => loadAllData(true)}
        refreshing={isRefreshing}
      />

      {campaign.campaign_status === 'failed' && campaign.user_facing_failure_reason?.trim() && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <ReactMarkdown
            components={{
              p: ({ children }) => <>{children}</>,
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>
                  {children}
                </a>
              ),
            }}
          >
            {campaign.user_facing_failure_reason}
          </ReactMarkdown>
        </Alert>
      )}

      {isExpired && !['failed', 'completed'].includes(campaign.campaign_status) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This campaign has expired at {campaign.end_at ? format(new Date(campaign.end_at), 'MMM dd, yyyy • h:mm a') : 'N/A'}.
        </Alert>
      )}

      {/* Stats Cards - Lead Level */}
      {leadStats && !editingSection && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Total Audience
                </Typography>
                <Typography variant="h4">{leadStats.total_leads}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Sent
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {leadStats.leads_sent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Delivered
                </Typography>
                <Typography variant="h4" color="success.main">
                  {leadStats.leads_delivered}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Read
                </Typography>
                <Typography variant="h4" color="info.main">
                  {leadStats.leads_read}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="h4" color="error.main">
                  {leadStats.leads_failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Rate Cards Row */}
      {leadStats && !editingSection && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Delivery Rate
                </Typography>
                <Typography variant="h4">{leadStats.delivery_rate}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography color="textSecondary" gutterBottom>
                  Read Rate
                </Typography>
                <Typography variant="h4" color="info.main">{leadStats.read_rate}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Button Responses Section */}
      {campaignStats?.button_stats && campaignStats.button_stats.length > 0 && !editingSection && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Template Button Click Stats
          </Typography>
          <Grid container spacing={2}>
            {campaignStats.button_stats.map((stat, idx) => {
              return (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                        {stat.button_text}
                      </Typography>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
                        {stat.count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      )}

      {/* Inline Editing Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Campaign Information Section */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Campaign Information
              </Typography>
              {canEdit && editingSection !== 'info' && !isRunning && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleSectionEdit('info')}
                  size="small"
                  disabled={isRunning}
                >
                  Edit
                </Button>
              )}
            </Box>
            {editingSection === 'info' ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Campaign Name"
                      fullWidth
                      value={editData.name || ''}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '56px' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ minWidth: 150 }}>
                        Campaign Type:
                      </Typography>
                      <Typography variant="body1">
                        {editData.campaign_type || 'Broadcast'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      value={editData.description || ''}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSectionSave('info')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSectionCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 150, fontWeight: 600, color: '#555' }}>
                      Name:
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {campaign.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 150, fontWeight: 600, color: '#555' }}>
                      Type:
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {campaign.campaign_type}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 150, fontWeight: 600, color: '#555' }}>
                      Description:
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1, color: '#666' }}>
                      {(campaign as any).description || 'No description'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Template Section */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              {/* {canEdit && editingSection !== 'template' && !isRunning && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleSectionEdit('template')}
                  size="small"
                  disabled={isRunning}
                >
                  Edit
                </Button>
              )} */}
            </Box>
            {editingSection === 'template' ? (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select Template</InputLabel>
                  <Select
                    value={editData.template_id || ''}
                    label="Select Template"
                    onChange={(e) => handleTemplateChange(e.target.value)}
                  >
                    {templates.length === 0 ? (
                      <MenuItem value="" disabled>
                        No templates available
                      </MenuItem>
                    ) : (
                      templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} ({template.language})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {selectedTemplate && templateVariables.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Template: {selectedTemplate.name} - {templateVariables.length} variables detected
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSectionSave('template')}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSectionCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Grid container spacing={isMobile ? 6 : 0}>
                {selectedTemplate ? (
                  <>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', mb:2}}>
                       <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Template
                       </Typography>
                       </Box>
                      <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                          Name:
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {selectedTemplate.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                          Category:
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {selectedTemplate.category}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                          Language:
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {selectedTemplate.language}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                          Status:
                        </Typography>
                        <Chip
                          label={selectedTemplate.template_status}
                          size="small"
                          color={selectedTemplate.template_status === 'approved' ? 'success' : 'default'}
                        />
                      </Box>
                      {selectedTemplate.variable_count > 0 && (
                        <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                            Variables:
                          </Typography>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {selectedTemplate.variable_count} variable{selectedTemplate.variable_count > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{mt:6}}>
                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Audience
                          </Typography>
                        </Box>
                        <Box>
                          {(campaign as any).audience_filter?.audience_type === 'master' ? (
                            <Box>
                              <Chip 
                                label="Master Lead Book" 
                                color="primary" 
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="body2" color="textSecondary">
                                Using Master Lead Book for this SMB
                              </Typography>
                            </Box>
                          ) : (campaign as any).leadbook ? (
                            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
                              <Typography variant="body2" sx={{ minWidth: !isMobile ? 150 : 100, fontWeight: 600, color: '#555' }}>
                                Uploaded CSV File:
                              </Typography>
                              <Box>
                              <Typography variant="body2" sx={{flex: 1}}>
                                {(campaign as any).leadbook.original_filename}
                              </Typography>
                               {(campaign as any).leadbook.uploaded_at && (
                                <Typography variant="caption" color="textSecondary">
                                  Uploaded {format(new Date((campaign as any).leadbook.uploaded_at), 'MMM dd, yyyy')}
                                </Typography>
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No audience uploaded
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <TemplatePreview 
                          template={selectedTemplate} 
                          align="left"
                        />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      No template attached
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Audience Section */}
        {/* <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Audience
              </Typography>
              {canEdit && editingSection !== 'audience' && !isRunning && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={() => handleSectionEdit('audience')}
                  size="small"
                  disabled={isRunning}
                >
                  Edit
                </Button>
              )}
            </Box>
            {editingSection === 'audience' ? (
              <Box>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <FormLabel>Audience Source</FormLabel>
                  <RadioGroup
                    row
                    value={editData.audience_type || ((campaign as any).audience_filter?.audience_type || 'upload')}
                    onChange={(e) => setEditData({ ...editData, audience_type: e.target.value })}
                  >
                    <FormControlLabel value="upload" control={<Radio />} label="Upload CSV" />
                    <FormControlLabel value="master" control={<Radio />} label="Use Master Lead Book" />
                  </RadioGroup>
                  {editData.audience_type === 'master' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Using Master Lead Book for this campaign. All leads from the Master Lead Book for this SMB will be included.
                    </Alert>
                  )}
                </FormControl>

                {editData.audience_type !== 'master' && (
                  <>
                    {existingLeadbook && !csvFile && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Current leadbook: {existingLeadbook.original_filename || 'uploaded file'}
                        {existingLeadbook.uploaded_at && ` (uploaded ${new Date(existingLeadbook.uploaded_at).toLocaleDateString()})`}
                      </Alert>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 2 }}
                    >
                      {existingLeadbook ? 'Replace CSV File' : 'Upload CSV File'}
                      <input
                        type="file"
                        hidden
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) setCsvFile(file)
                        }}
                      />
                    </Button>
                    {csvFile && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        ✓ {csvFile.name} ready to upload {existingLeadbook && '(will replace existing)'}
                      </Alert>
                    )}
                  </>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSectionSave('audience')}
                    disabled={saving || (editData.audience_type !== 'master' && !csvFile && !existingLeadbook)}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSectionCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                {(campaign as any).audience_filter?.audience_type === 'master' ? (
                  <Box>
                    <Chip 
                      label="Master Lead Book" 
                      color="primary" 
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      Using Master Lead Book for this SMB
                    </Typography>
                  </Box>
                ) : (campaign as any).leadbook ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Uploaded CSV File:
                    </Typography>
                    <Typography variant="body2">
                      {(campaign as any).leadbook.original_filename}
                    </Typography>
                    {(campaign as any).leadbook.uploaded_at && (
                      <Typography variant="caption" color="textSecondary">
                        Uploaded {format(new Date((campaign as any).leadbook.uploaded_at), 'MMM dd, yyyy')}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No audience uploaded
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card> */}

        {/* Schedule Section */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Schedule
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {canEdit && editingSection !== 'schedule' && !isRunning && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => handleSectionEdit('schedule')}
                    size="small"
                    disabled={isRunning}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            </Box>
            {editingSection === 'schedule' ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TimezoneSelector
                      value={editData.timezone || 'IST'}
                      onChange={(value) => setEditData({ ...editData, timezone: value })}
                      disabled={wasPartiallyCompleted}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Start Date & Time"
                      type="datetime-local"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editData.start_at || getDefaultStartDate()}
                      disabled={wasPartiallyCompleted}
                      onChange={(e) => {
                        const value = e.target.value
                        setEditData((prev: any) => ({ ...prev, start_at: value }))
                      }}
                    />
                  </Grid>
                  {/* Always show End Date for all campaigns */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="End Date & Time"
                      type="datetime-local"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      value={editData.end_at || getDefaultEndDate()}
                      onChange={(e) => {
                        const value = e.target.value
                        setEditData((prev: any) => ({ ...prev, end_at: value }))
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Send Window Start"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editData.send_window_start || '09:00'}
                      onChange={(e) => {
                        const value = e.target.value
                        setEditData((prev: any) => ({ ...prev, send_window_start: value }))
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Send Window End"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={editData.send_window_end || '18:00'}
                      onChange={(e) => {
                        const value = e.target.value
                        setEditData((prev: any) => ({ ...prev, send_window_end: value }))
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSectionSave('schedule')}
                    disabled={saving}
                  >
                    {saving ? (wasPartiallyCompleted ? 'Saving...' : 'Scheduling...') : (wasPartiallyCompleted ? 'Save' : 'Schedule')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSectionCancel}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Start:</strong> {campaign.start_at ? format(new Date(campaign.start_at), 'PPpp') : format(new Date(getDefaultStartDate()), 'PPpp')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>End:</strong> {(campaign as any).end_at ? format(new Date((campaign as any).end_at), 'PPpp') : format(new Date(getDefaultEndDate()), 'PPpp')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Timezone:</strong> {campaign.timezone || 'IST'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Send Window:</strong> {formatTime12Hour((campaign as any).send_window_start || '09:00')} - {formatTime12Hour((campaign as any).send_window_end || '18:00')}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Messages and Execution Logs Tabs */}
      <Card sx={{ mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
          >
            <Tab label="Messages" />
            <Tab label="Execution Logs" />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={activeTab} index={0}>
            {id && (
              <CampaignMessageStatsTab
                campaignId={id}
                onOpenConversation={handleOpenConversation}
                isRefreshing={isRefreshing && activeTab === 0}
              />
            )}
          </TabPanel>

            <TabPanel value={activeTab} index={1}>
              {executionLogsLoading && !executionLogsLoaded ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ maxHeight: 600, minHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 180 }}>
                        Started At
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 180 }}>
                        Completed At
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 120 }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 100 }}>
                        Total Leads
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 100 }}>
                        Pending
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 100, color: 'success.main' }}>
                        Successful
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f9fafb', minWidth: 100, color: 'error.main' }}>
                        Failed
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {executionLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="textSecondary">
                            No execution logs yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      executionLogs.map((log) => (
                        <TableRow 
                          key={log.id}
                          sx={{ 
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                          }}
                        >
                          <TableCell>
                            {format(new Date(log.execution_started_at), 'MMM dd, yyyy hh:mm a')}
                          </TableCell>
                          <TableCell>
                            {log.execution_completed_at 
                              ? format(new Date(log.execution_completed_at), 'MMM dd, yyyy hh:mm a')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <CampaignStatusChip status={log.status} />
                          </TableCell>
                          <TableCell>{log.total_leads || 0}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {log.pending_leads || 0}
                          </TableCell>
                          <TableCell sx={{ color: 'success.main', fontWeight: 500 }}>
                            {log.successful_leads || 0}
                          </TableCell>
                          <TableCell sx={{ color: 'error.main', fontWeight: 500 }}>
                            {log.failed_leads || 0}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </TabPanel>
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${campaign.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />

      {/* Conversation Detail Dialog */}
      <ConversationDetailDialog
        open={conversationDialogOpen}
        onClose={handleCloseConversationDialog}
        conversationId={selectedConversationId}
        smbId={campaign?.smb_id}
      />
    </Box>
  )
}

export default CampaignDetail
